<script setup lang="ts">
import { ref, inject, computed, watch, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTabComposable } from '@/components/tabs/useTabComposable';
import { useIndexStore } from '@/store/IndexStore';
import Button from 'primevue/button';

const dialogRef = inject('dialogRef') as Ref;
const closeModal = dialogRef?.value?.options.emits.closeModal;
const submitSuccess = dialogRef?.value?.options.emits.submitSuccess;

const { t } = useI18n({ useScope: 'global' });
const { loadingIcon } = useTabComposable();
const indexStore = useIndexStore();
const timeless = ref(90);
const stopWait = ref(false);

const message = computed(() => indexStore.websocketMessages);

const stepConstant = {
  stepOne: 1,
  stepTwo: 2,
  stepThree: 3
};

const processStatusSteps = {
  [stepConstant.stepOne]: {
    isFinished: false,
    processingMessage: t('advance_quote.form.validating_insta_quote'),
    finishedMessage: t('advance_quote.form.finished_validate_quote')
  },
  [stepConstant.stepTwo]: {
    isFinished: false,
    processingMessage: t('advance_quote.form.retrieving_price'),
    finishedMessage: t('advance_quote.form.retrieving_success')
  },
  [stepConstant.stepThree]: {
    isFinished: false,
    processingMessage: t('advance_quote.form.generating_pdf')
  }
};

const processStatus = ref([
  {
    isFinished: false,
    processingMessage: t('advance_quote.form.validating_insta_quote'),
    finishedMessage: t('advance_quote.form.finished_validate_quote')
  }
]);

const updateData = (newValue) => {
  processStatus.value[newValue.step - 1].isFinished = newValue.status;
  if (newValue.status && newValue.step !== stepConstant.stepThree) {
    processStatus.value.push(processStatusSteps[newValue.step + 1]);
  } else {
    submitSuccess({ quoteId: newValue.quoteId, pdfUrl: newValue.pdfUrl });
  }
};
watch(
  message,
  (newValue) => {
    updateData(newValue);
  },
  {
    deep: true
  }
);

const onCountDown = () => {
  const countDown = setInterval(() => {
    if (timeless.value === 0) {
      clearInterval(countDown);
      if (!processStatus.value[1]?.isFinished) {
        stopWait.value = true;
      }
    } else {
      timeless.value--;
    }
  }, 1000);
};

const continueWait = () => {
  stopWait.value = false;
  timeless.value = 90;
  onCountDown();
};
onCountDown();
</script>
<template>
  <div class="insta-process-modal">
    <div class="text-center text-2xl font-bold mt-5 mb-3">{{ $t('advance_quote.form.process_your_quote') }}</div>
    <div v-if="!stopWait">
      <div v-for="(status, sIndex) in processStatus" :key="'status' + sIndex" class="my-3">
        <div class="flex align-items-center gap-2">
          <i v-if="status.isFinished" class="pi pi-check-circle text-green-700 text-2xl"></i>
          <img v-else class="loading-icon rotate" :src="loadingIcon" alt="loading" />
          <span>{{ status.isFinished ? status.finishedMessage : status.processingMessage }}</span>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="description">{{ $t('advance_quote.form.time_exceeds_90s') }}</div>
      <div class="flex justify-content-between gap-3 w-full mt-3">
        <Button class="p-3" :label="$t('advance_quote.form.continue_waiting')" icon="pi pi-eye" @click="continueWait()"></Button>
        <Button class="p-3" :label="$t('advance_quote.form.view_in_my_quote')" icon="pi pi-check" @click="closeModal()"></Button>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.insta-process-modal {
  .loading-icon {
    height: 1.75rem;
    width: 1.75rem;
    margin: 0 -0.125rem;
  }

  .description {
    white-space: pre-line;
    line-height: 150%;
  }

  @keyframes loader {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }

  .rotate {
    animation: loader infinite 0.8s linear;
  }
}
</style>
